import { useState, useEffect, useMemo } from "react";
import { FLAGS, VALID_UNTIL } from "../lib/featureFlags";

/**
 * FoundingValidityTimer — live 14-day countdown from TODAY (not proposal.created_at).
 * - Anchor locked once on mount for stable expiry date across re-renders.
 * - Ticks every second; shows D / H / M / S + expiry date.
 * - `variant="compact"` renders a condensed inline version for the Full Breakdown.
 */
const FoundingValidityTimer = ({ variant = "default" }) => {
  const lang = typeof window !== 'undefined' ? (localStorage.getItem('sp_lang') || 'en') : 'en';
  const t = (en, de) => (lang === 'de' ? de : en);

  // Anchor: lock "now" once on mount → today + 14 days, but never later than the Founding Window (Aug 31, 2026)
  const validUntil = useMemo(() => {
    const in14 = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    const foundingWindowEnd = new Date("2026-08-31T23:59:59Z");
    return in14 < foundingWindowEnd ? in14 : foundingWindowEnd;
  }, []);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const diffMs = validUntil.getTime() - now.getTime();
  const expired = diffMs <= 0;
  const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  const hours = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
  const minutes = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)));
  const seconds = Math.max(0, Math.floor((diffMs % (1000 * 60)) / 1000));

  const validDate = validUntil.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  // When live countdown is disabled, show a static date instead
  const staticEnabled = !FLAGS.showLiveCountdown;
  const displayDate = staticEnabled ? VALID_UNTIL : validDate;

  const units = [
    { v: days, l: t("D", "T") },
    { v: hours, l: t("H", "Std") },
    { v: minutes, l: t("M", "M") },
    { v: seconds, l: t("S", "S") },
  ];

  if (variant === "compact") {
    return (
      <div
        className="flex items-center gap-3 px-3.5 py-2 rounded-lg"
        style={{
          background: 'linear-gradient(90deg, rgba(232,200,114,0.06), rgba(8,8,12,0.4))',
          border: '1px solid rgba(232,200,114,0.18)',
        }}
        data-testid="founding-validity-timer-compact"
      >
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="w-1 h-1 rounded-full animate-pulse" style={{ background: '#E8C872' }} />
          <p className="text-[#E8C872]/80 text-[8px] tracking-[0.28em] uppercase font-bold whitespace-nowrap">
            {t("Validity", "Gültigkeit")}
          </p>
        </div>
        {!expired && !staticEnabled && (
          <div className="flex items-center gap-1 tabular-nums">
            {units.map((u, i) => (
              <span key={i} className="flex items-baseline gap-0.5">
                <span className="text-[11px] font-light leading-none" style={{ color: '#E8C872' }}>{String(u.v).padStart(2, '0')}</span>
                <span className="text-[7px] tracking-[0.14em] uppercase font-bold text-white/40">{u.l}</span>
              </span>
            ))}
          </div>
        )}
        <div className="h-3 w-px" style={{ background: 'rgba(232,200,114,0.18)' }} />
        <p className="text-[9px] tracking-[0.2em] uppercase font-semibold whitespace-nowrap" style={{ color: '#E8C872' }}>
          {staticEnabled ? `Valid until ${displayDate}` : displayDate}
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-xl overflow-hidden flex items-center justify-between gap-6 px-5 py-3"
      style={{
        background: 'linear-gradient(90deg, rgba(232,200,114,0.08), rgba(8,8,12,0.6))',
        border: '1px solid rgba(232,200,114,0.22)',
      }}
      data-testid="founding-validity-timer"
    >
      {/* LEFT — heading + 1-line explanation */}
      <div className="flex items-center gap-4 min-w-0">
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#E8C872' }} />
          <p className="text-[#E8C872]/85 text-[9px] tracking-[0.3em] uppercase font-bold whitespace-nowrap">
            {t("Proposal Validity", "Angebotsgültigkeit")}
          </p>
        </div>
        <div className="h-5 w-px shrink-0" style={{ background: 'rgba(232,200,114,0.18)' }} />
        <p className="text-white/70 text-[12px] font-light leading-tight truncate">
          {expired
            ? t("Founding window closed — pricing and positioning no longer protected.", "Fenster geschlossen — Preise und Positionierung nicht mehr geschützt.")
            : t("Founding conditions remain protected until this date.", "Gründer-Konditionen bleiben bis zu diesem Datum geschützt.")}
        </p>
      </div>

      {/* RIGHT — timer boxes + expiry date */}
      <div className="flex items-center gap-3 shrink-0">
        {!expired && !staticEnabled && (
          <div className="flex items-center gap-1.5" data-testid="founding-timer-countdown">
            {units.map((u, i) => (
              <div key={i} className="flex items-baseline gap-1 px-2.5 py-1 rounded-md" style={{ background: 'rgba(232,200,114,0.08)', border: '1px solid rgba(232,200,114,0.16)' }}>
                <span className="text-[15px] font-light tabular-nums leading-none" style={{ color: '#E8C872' }}>{String(u.v).padStart(2, '0')}</span>
                <span className="text-[8px] tracking-[0.18em] uppercase font-bold text-white/45">{u.l}</span>
              </div>
            ))}
          </div>
        )}
        <div className="h-5 w-px" style={{ background: 'rgba(232,200,114,0.18)' }} />
        <p className="text-[12px] tracking-[0.22em] uppercase font-semibold whitespace-nowrap" style={{ color: '#E8C872' }}>
          {staticEnabled ? `Valid until ${displayDate}` : displayDate}
        </p>
      </div>
    </div>
  );
};

export default FoundingValidityTimer;
